import {
    EonUiButton,
    EonUiDropdown,
    EonUiDropdownOption,
    EonUiIcon,
    EonUiInput,
} from "@eon-ui/eon-ui-components-react";
import { EnvironmentConfig } from "@me8eon/config";
import { useJwtToken } from "@me8eon/react_authentication";
import { useState, useEffect } from "react";
import useOnboardingPortalStore from "./OnboardingPortal.Store";
import { OnboardingCompaniesList } from "./OnboardingPortal.constants";
import {
    sanitizeCompanyList,
    getCompanyPayloadList,
    getCompanySelectionForForm,
    encodeCompanyOptionValue,
    isOtherSelected,
} from "./OnboardingPortal.utils";
import OnboardingFormLabel from "./components/OnboardingFormLabel";
import { useTranslation } from "@me8eon/translation";
import { OnboardingQnAData } from "./OnboardingPortal.types";

export interface QnAData {
    id: string;
    question: string;
    answer: string;
    category: string;
    categoryOther?: string;
    companiesAllowed: string;
    companiesRestricted: string;
    companiesAllowedOther?: string;
    companiesRestrictedOther?: string;
    invalidQuestion?: boolean;
    invalidAnswer?: boolean;
    invalidCategory?: boolean;
    isOpen?: boolean;
    isEditable?: boolean;
}

type QnAPayloadItem = {
    id?: string;
    question: string;
    answer: string;
    category: string;
    companiesAllowed: string[];
    companiesRestricted: string[];
};

const QNA_CATEGORY_OPTIONS = ["ITSM", "HRAdmin"];

const emptyQnAItem = {
    id: "",
    question: "",
    answer: "",
    category: "",
    categoryOther: "",
    companiesAllowed: "",
    companiesRestricted: "",
    companiesAllowedOther: "",
    companiesRestrictedOther: "",
    invalidQuestion: false,
    invalidAnswer: false,
    invalidCategory: false,
    isOpen: true,
    isEditable: true,
};

const QnAOnboardingForm = ({
    qnaData,
    onSubmit,
    onCancel,
    isEditing = false,
}: {
    qnaData?: OnboardingQnAData | null;
    onSubmit: () => void;
    onCancel?: () => void;
    isEditing?: boolean;
}) => {
    const jwt = useJwtToken();
    const t = useTranslation();
    const { createNewQnA, showModule } = useOnboardingPortalStore();
    const [formData, setFormData] = useState<QnAData[]>([emptyQnAItem]);

    useEffect(() => {
        setFormData([emptyQnAItem]);
    }, [showModule]);

    useEffect(() => {
        if (qnaData?.id && isEditing) {
            const {
                id,
                question,
                answer,
                category,
                companiesAllowed,
                companiesRestricted,
            } = qnaData;
            const allowedSelection = getCompanySelectionForForm(
                companiesAllowed as string | string[] | undefined,
                OnboardingCompaniesList
            );
            const restrictedSelection = getCompanySelectionForForm(
                companiesRestricted as string | string[] | undefined,
                OnboardingCompaniesList
            );
            const isKnownCategory = QNA_CATEGORY_OPTIONS.includes(
                category ?? ""
            );
            const resolvedCategory = isKnownCategory
                ? (category ?? "")
                : category
                  ? "Other"
                  : "";
            const resolvedCategoryOther = isKnownCategory
                ? ""
                : (category ?? "");
            const tempQnAData: QnAData = {
                id: id ?? "",
                question: question ?? "",
                answer: answer ?? "",
                category: resolvedCategory,
                categoryOther: resolvedCategoryOther,
                companiesAllowed: allowedSelection.selectedCompanies,
                companiesRestricted: restrictedSelection.selectedCompanies,
                companiesAllowedOther: allowedSelection.otherCompanies,
                companiesRestrictedOther: restrictedSelection.otherCompanies,
                isOpen: true,
                isEditable: true,
            };
            setFormData([tempQnAData]);
        }
    }, [qnaData, isEditing]);

    useEffect(() => {
        if (createNewQnA > 0) {
            setFormData((prevData) => [...prevData, emptyQnAItem]);
        }
    }, [createNewQnA]);

    const resetForm = () => {
        setFormData([emptyQnAItem]);
    };

    const validateForm = (idx: number): boolean => {
        const qnaItem = formData[idx];
        const hasInvalidFields =
            !qnaItem.question?.trim() ||
            !qnaItem.answer?.trim() ||
            !qnaItem.category?.trim();
        if (hasInvalidFields) {
            setFormData(
                formData.map((item, index) =>
                    index === idx
                        ? {
                              ...item,
                              invalidQuestion: !item.question?.trim()
                                  ? true
                                  : item.invalidQuestion,
                              invalidAnswer: !item.answer?.trim()
                                  ? true
                                  : item.invalidAnswer,
                              invalidCategory: !item.category?.trim()
                                  ? true
                                  : item.invalidCategory,
                          }
                        : item
                )
            );
            return false;
        }
        return true;
    };

    const handleSubmit = async (idx: number) => {
        if (!validateForm(idx)) {
            return;
        }

        const tempItem = formData[idx];
        const resolvedCategory =
            tempItem.category === "Other"
                ? (tempItem.categoryOther ?? "").trim()
                : tempItem.category;
        const payload: QnAPayloadItem = {
            question: tempItem.question,
            answer: tempItem.answer,
            category: resolvedCategory,
            companiesAllowed: getCompanyPayloadList(
                tempItem.companiesAllowed,
                tempItem.companiesAllowedOther ?? ""
            ),
            companiesRestricted: getCompanyPayloadList(
                tempItem.companiesRestricted,
                tempItem.companiesRestrictedOther ?? ""
            ),
        };
        if (tempItem.id) {
            payload.id = tempItem.id;
        }

        const tempQnAData: QnAPayloadItem[] = [payload];

        const API_BASE_URL = EnvironmentConfig.nodeService;
        const API_ENDPOINT = `${API_BASE_URL}/service/onboarding/qna`;

        console.log("Submitting app data:", tempQnAData);
        try {
            const token = await jwt();
            const response = await fetch(API_ENDPOINT, {
                method: tempItem.id ? "PUT" : "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    qna: tempQnAData,
                }),
            });
            if (!response.ok) {
                const errorBody = await response.json();
                throw new Error(
                    `Failed to set qna data: ${response.status} ${response.statusText}${errorBody?.error ? ` - ${errorBody.error}` : ""}`
                );
            }
            const data: { isSuccess: boolean; qna: QnAData[] } =
                await response.json();
            if (data.isSuccess !== true) {
                console.warn(
                    "API call succeeded, but response not marked as success."
                );
            }
            setFormData((prevData) => [
                ...prevData.map((item, index) =>
                    index === idx
                        ? {
                              ...item,
                              isEditable: false,
                              isOpen: false,
                              id: tempItem.id ? tempItem.id : data.qna[0].id,
                          }
                        : item
                ),
                emptyQnAItem
            ]);
            alert(t("onboardingPortal.qnaOnboardingForm.successAlert"));
            onSubmit();
        } catch (error) {
            console.error("Error in handleSubmit:", error);
            throw error;
        }

        if (isEditing && tempItem.id && onCancel) {
            onCancel();
        }
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column-reverse",
                gap: "16px",
                backgroundColor: "#ffffff",
            }}
        >
            <p>{t("onboardingPortal.qnaOnboardingForm.message")}</p>
            {formData.map((qnaItem, index) =>
                qnaItem.isOpen ? (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "16px",
                            backgroundColor: "#fafafa",
                            padding: "16px",
                        }}
                        key={qnaItem.id ?? `index-${index}`}
                    >
                        <form className="onboarding-portal--form" key={index}>
                            <div className="onboarding-portal--form--row">
                                <div className="onboarding-portal--form--field">
                                    <OnboardingFormLabel
                                        label={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaQuestionLabel"
                                        )}
                                        tooltip={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaQuestionTooltip"
                                        )}
                                    />
                                    <EonUiInput
                                        type="text"
                                        name={`question-${index}`}
                                        value={qnaItem.question}
                                        placeholder={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaQuestionPlaceholder"
                                        )}
                                        required
                                        invalid={qnaItem.invalidQuestion}
                                        errorMessage={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaQuestionError"
                                        )}
                                        onValueChanged={(e) => {
                                            setFormData((prevData) =>
                                                prevData.map((item, idx) =>
                                                    idx === index
                                                        ? {
                                                              ...item,
                                                              question:
                                                                  e.target
                                                                      .value,
                                                              invalidQuestion:
                                                                  item.invalidQuestion
                                                                      ? !e.target.value.trim()
                                                                      : false,
                                                          }
                                                        : item
                                                )
                                            );
                                        }}
                                        disabled={!qnaItem.isEditable}
                                    />
                                </div>
                            </div>
                            <div className="onboarding-portal--form--row">
                                <div className="onboarding-portal--form--field">
                                    <OnboardingFormLabel
                                        label={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaAnswerLabel"
                                        )}
                                        tooltip={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaAnswerTooltip"
                                        )}
                                    />
                                    <EonUiInput
                                        textarea
                                        rows={2}
                                        type="text"
                                        name={`answer-${index}`}
                                        value={qnaItem.answer}
                                        placeholder={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaAnswerPlaceholder"
                                        )}
                                        required
                                        invalid={qnaItem.invalidAnswer}
                                        errorMessage={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaAnswerError"
                                        )}
                                        onValueChanged={(e) => {
                                            setFormData((prevData) =>
                                                prevData.map((item, idx) =>
                                                    idx === index
                                                        ? {
                                                              ...item,
                                                              answer: e.target
                                                                  .value,
                                                              invalidAnswer:
                                                                  item.invalidAnswer
                                                                      ? !e.target.value.trim()
                                                                      : false,
                                                          }
                                                        : item
                                                )
                                            );
                                        }}
                                        disabled={!qnaItem.isEditable}
                                    />
                                </div>
                            </div>
                            <div className="onboarding-portal--form--row">
                                <div className="onboarding-portal--form--field">
                                    <OnboardingFormLabel
                                        label={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaCategoryLabel"
                                        )}
                                        tooltip={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaCategoryTooltip"
                                        )}
                                    />
                                    <EonUiDropdown
                                        value={qnaItem.category}
                                        invalid={qnaItem.invalidCategory}
                                        errorMessage={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaCategoryError"
                                        )}
                                        onValueChanged={(e) => {
                                            setFormData((prevData) =>
                                                prevData.map((item, idx) =>
                                                    idx === index
                                                        ? {
                                                              ...item,
                                                              category:
                                                                  e.target
                                                                      .value,
                                                              invalidCategory:
                                                                  item.invalidCategory
                                                                      ? !e.target.value.trim()
                                                                      : false,
                                                          }
                                                        : item
                                                )
                                            );
                                        }}
                                        key={`category-${index}-${qnaItem.category}`}
                                        disabled={!qnaItem.isEditable}
                                    >
                                        {QNA_CATEGORY_OPTIONS.map((cat) => (
                                            <EonUiDropdownOption
                                                key={cat}
                                                value={cat}
                                                label={cat}
                                            />
                                        ))}
                                        <EonUiDropdownOption
                                            key="Other"
                                            value="Other"
                                            label="Other"
                                        />
                                    </EonUiDropdown>
                                    {qnaItem.category === "Other" && (
                                        <EonUiInput
                                            type="text"
                                            value={qnaItem.categoryOther}
                                            label={t(
                                                "onboardingPortal.qnaOnboardingForm.qnaCategoryOtherLabel"
                                            )}
                                            placeholder={t(
                                                "onboardingPortal.qnaOnboardingForm.qnaCategoryOtherPlaceholder"
                                            )}
                                            labelOutside
                                            autocomplete={false}
                                            onValueChanged={(e) => {
                                                setFormData((prevData) =>
                                                    prevData.map((item, idx) =>
                                                        idx === index
                                                            ? {
                                                                  ...item,
                                                                  categoryOther:
                                                                      e.target
                                                                          .value,
                                                              }
                                                            : item
                                                    )
                                                );
                                            }}
                                            disabled={!qnaItem.isEditable}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="onboarding-portal--form--row">
                                <div className="onboarding-portal--form--field">
                                    <OnboardingFormLabel
                                        label={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaCompaniesAllowedLabel"
                                        )}
                                        tooltip={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaCompaniesAllowedTooltip"
                                        )}
                                    />
                                    <EonUiDropdown
                                        info
                                        infoMessage={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaCompaniesAllowedInfoMessage"
                                        )}
                                        value={qnaItem.companiesAllowed}
                                        multiselect
                                        showSelectedItems
                                        onValueChanged={(e) => {
                                            setFormData((prevData) =>
                                                prevData.map((item, idx) =>
                                                    idx === index
                                                        ? {
                                                              ...item,
                                                              companiesAllowed:
                                                                  e.target
                                                                      .value,
                                                          }
                                                        : item
                                                )
                                            );
                                        }}
                                        height={"200px"}
                                        key={qnaItem.companiesAllowed}
                                        disabled={!qnaItem.isEditable}
                                    >
                                        {OnboardingCompaniesList.map(
                                            (company) => (
                                                <EonUiDropdownOption
                                                    key={company}
                                                    value={encodeCompanyOptionValue(
                                                        company
                                                    )}
                                                    label={company}
                                                />
                                            )
                                        )}
                                        <EonUiDropdownOption
                                            key="Other"
                                            value="Other"
                                            label="Other"
                                        />
                                    </EonUiDropdown>
                                    {isOtherSelected(
                                        qnaItem.companiesAllowed
                                    ) && (
                                        <EonUiInput
                                            type="text"
                                            value={
                                                qnaItem.companiesAllowedOther
                                            }
                                            label={t(
                                                "onboardingPortal.qnaOnboardingForm.qnaCompaniesAllowedOtherLabel"
                                            )}
                                            placeholder={t(
                                                "onboardingPortal.qnaOnboardingForm.qnaCompaniesAllowedOtherPlaceholder"
                                            )}
                                            labelOutside
                                            autocomplete={false}
                                            onValueChanged={(e) => {
                                                setFormData((prevData) =>
                                                    prevData.map((item, idx) =>
                                                        idx === index
                                                            ? {
                                                                  ...item,
                                                                  companiesAllowedOther:
                                                                      e.target
                                                                          .value,
                                                              }
                                                            : item
                                                    )
                                                );
                                            }}
                                            disabled={!qnaItem.isEditable}
                                        />
                                    )}
                                </div>
                                {/* <div className="onboarding-portal--form--field">
                                    <OnboardingFormLabel
                                        label={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaCompaniesRestrictedLabel"
                                        )}
                                        tooltip={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaCompaniesRestrictedTooltip"
                                        )}
                                    />
                                    <EonUiDropdown
                                        info
                                        infoMessage={t(
                                            "onboardingPortal.qnaOnboardingForm.qnaCompaniesRestrictedInfoMessage"
                                        )}
                                        value={qnaItem.companiesRestricted}
                                        multiselect
                                        showSelectedItems
                                        onValueChanged={(e) => {
                                            setFormData((prevData) =>
                                                prevData.map((item, idx) =>
                                                    idx === index
                                                        ? {
                                                              ...item,
                                                              companiesRestricted:
                                                                  e.target
                                                                      .value,
                                                          }
                                                        : item
                                                )
                                            );
                                        }}
                                        height={"200px"}
                                        key={qnaItem.companiesRestricted}
                                        disabled={!qnaItem.isEditable}
                                    >
                                        {OnboardingCompaniesList.map(
                                            (company) => (
                                                <EonUiDropdownOption
                                                    key={company}
                                                    value={encodeCompanyOptionValue(
                                                        company
                                                    )}
                                                    label={company}
                                                />
                                            )
                                        )}
                                        <EonUiDropdownOption
                                            value="Other"
                                            label="Other"
                                        />
                                    </EonUiDropdown>
                                    {isOtherSelected(
                                        qnaItem.companiesRestricted
                                    ) && (
                                        <EonUiInput
                                            type="text"
                                            value={
                                                qnaItem.companiesRestrictedOther
                                            }
                                            label={t(
                                                "onboardingPortal.qnaOnboardingForm.qnaCompaniesRestrictedOtherLabel"
                                            )}
                                            placeholder={t(
                                                "onboardingPortal.qnaOnboardingForm.qnaCompaniesRestrictedOtherPlaceholder"
                                            )}
                                            labelOutside
                                            autocomplete={false}
                                            onValueChanged={(e) => {
                                                setFormData((prevData) =>
                                                    prevData.map((item, idx) =>
                                                        idx === index
                                                            ? {
                                                                  ...item,
                                                                  companiesRestrictedOther:
                                                                      e.target
                                                                          .value,
                                                              }
                                                            : item
                                                    )
                                                );
                                            }}
                                            disabled={!qnaItem.isEditable}
                                        />
                                    )}
                                </div> */}
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "8px",
                                }}
                            >
                                {qnaItem?.isEditable ? (
                                    <>
                                        <EonUiButton
                                            text={
                                                qnaData?.id
                                                    ? t(
                                                          "onboardingPortal.qnaOnboardingForm.updateQnABtn"
                                                      )
                                                    : t(
                                                          "onboardingPortal.qnaOnboardingForm.submitQnABtn"
                                                      )
                                            }
                                            rank="primary"
                                            size="small"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleSubmit(index);
                                            }}
                                        />
                                        {qnaItem.id && (
                                            <EonUiButton
                                                text={t(
                                                    "onboardingPortal.qnaOnboardingForm.cancelBtn"
                                                )}
                                                rank="secondary"
                                                size="small"
                                                onClick={() => {
                                                    if (onCancel) {
                                                        onCancel();
                                                    }
                                                    // else resetForm();
                                                    else {
                                                        setFormData(
                                                            (prevData) =>
                                                                prevData.map(
                                                                    (
                                                                        item,
                                                                        idx
                                                                    ) =>
                                                                        idx ===
                                                                        index
                                                                            ? {
                                                                                  ...item,
                                                                                  isEditable: false,
                                                                                  isOpen: false,
                                                                              }
                                                                            : item
                                                                )
                                                        );
                                                    }
                                                }}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <EonUiButton
                                        text={t(
                                            "onboardingPortal.qnaOnboardingForm.editQnABtn"
                                        )}
                                        rank="secondary"
                                        size="small"
                                        onClick={() => {
                                            setFormData((prevData) =>
                                                prevData.map((item, idx) =>
                                                    idx === index
                                                        ? {
                                                              ...item,
                                                              isEditable: true,
                                                          }
                                                        : item
                                                )
                                            );
                                        }}
                                    />
                                )}
                                {!qnaItem.id && (
                                    <div
                                        onClick={() => {
                                            setFormData((prevData) =>
                                                prevData.filter(
                                                    (_, idx) => idx !== index
                                                )
                                            );
                                        }}
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            alignItems: "center",
                                            gap: "8px",
                                            cursor: "pointer",
                                            marginLeft: "auto",
                                        }}
                                    >
                                        <EonUiIcon
                                            name="trash_bin"
                                            scheme="red500"
                                        />
                                        <span style={{ color: "#ea1b0a" }}>
                                            Delete
                                        </span>
                                    </div>
                                )}
                            </div>
                        </form>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                                flexGrow: 1,
                                maxWidth: "25%",
                                minWidth: "200px",
                            }}
                        >
                            <div
                                style={{
                                    border: "1px solid #DBDADE",
                                    backgroundColor: "#FFFFFF",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "6px",
                                    padding: "16px",
                                    borderRadius: "8px",
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: "18px",
                                        fontWeight: 500,
                                        color: "#030712",
                                        borderBottom: "1px solid #E0E0E0",
                                        paddingBottom: "8px",
                                    }}
                                >
                                    Preview Q&A
                                </div>
                                <div className="onboarding-portal--form--preview">
                                    <div className="onboarding-portal--form--preview--details">
                                        <div className="onboarding-portal--form--preview--details--label">
                                            {t(
                                                "onboardingPortal.qnaOnboardingForm.qnaQuestionPreview"
                                            )}
                                            :
                                        </div>
                                        <div className="onboarding-portal--form--preview--details--value">
                                            {qnaItem.question}
                                        </div>

                                        <div className="onboarding-portal--form--preview--details--label">
                                            {t(
                                                "onboardingPortal.qnaOnboardingForm.qnaAnswerPreview"
                                            )}
                                            :
                                        </div>
                                        <div className="onboarding-portal--form--preview--details--value">
                                            {qnaItem.answer}
                                        </div>

                                        <div className="onboarding-portal--form--preview--details--label">
                                            Category:{" "}
                                        </div>
                                        <div className="onboarding-portal--form--preview--details--value">
                                            {qnaItem.category === "Other"
                                                ? qnaItem.categoryOther ||
                                                  "Other"
                                                : qnaItem.category}
                                        </div>

                                        <div className="onboarding-portal--form--preview--details--label">
                                            {t(
                                                "onboardingPortal.qnaOnboardingForm.qnaCompaniesAllowedPreview"
                                            )}
                                            :
                                        </div>
                                        <div className="onboarding-portal--form--preview--details--value">
                                            {sanitizeCompanyList(
                                                qnaItem.companiesAllowed,
                                                qnaItem.companiesAllowedOther ??
                                                    ""
                                            )}
                                        </div>
                                        {/* <div className="onboarding-portal--form--preview--details--label">
                                            {t(
                                                "onboardingPortal.qnaOnboardingForm.qnaCompaniesRestrictedPreview"
                                            )}
                                            :
                                        </div>
                                        <div className="onboarding-portal--form--preview--details--value">
                                            {sanitizeCompanyList(
                                                qnaItem.companiesRestricted,
                                                qnaItem.companiesRestrictedOther ??
                                                    ""
                                            )}
                                        </div> */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            padding: "16px",
                            gap: "20px",
                            backgroundColor: "#F9F6F4",
                            borderRadius: "8px",
                            cursor: "pointer",
                        }}
                        key={qnaItem.id ?? `index-${index}`}
                        onClick={() => {
                            setFormData((prevData) =>
                                prevData.map((item, idx) =>
                                    idx === index
                                        ? { ...item, isOpen: true }
                                        : item
                                )
                            );
                        }}
                    >
                        <div
                            style={{
                                fontSize: "16px",
                                fontWeight: 600,
                                flex: 1,
                            }}
                        >
                            {qnaItem.question}
                        </div>
                        <EonUiIcon
                            name="chevron_small_down"
                            style={{ flexShrink: 0 }}
                        />
                    </div>
                )
            )}
        </div>
    );
};

export default QnAOnboardingForm;
